// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import useAccessibleSubmit from "@insite/client-framework/Common/Hooks/useAccessibleSubmit";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateQuoteLine from "@insite/client-framework/Store/Pages/RfqQuoteDetails/Handlers/UpdateQuoteLine";
import translate from "@insite/client-framework/Translate";
import { QuoteLineModel, QuoteModel } from "@insite/client-framework/Types/ApiModels";
import ProductBrand, { ProductBrandStyles } from "@insite/content-library/Components/ProductBrand";
import ProductDescription, { ProductDescriptionStyles } from "@insite/content-library/Components/ProductDescription";
import ProductImage, { ProductImageStyles } from "@insite/content-library/Components/ProductImage";
import ProductPartNumbers, { ProductPartNumbersStyles } from "@insite/content-library/Components/ProductPartNumbers";
import SmallHeadingAndText, { SmallHeadingAndTextStyles } from "@insite/content-library/Components/SmallHeadingAndText";
import RfqQuoteDetailsQuotedPricing, {
    RfqQuoteDetailsQuotedPricingStyles,
} from "@insite/content-library/Widgets/RfqQuoteDetails/RfqQuoteDetailsQuotedPricing";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import TextArea, { TextAreaProps } from "@insite/mobius/TextArea";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import CartLineContainer, {
    CartLineContainerStyles,
    LineHeaderStyles,
} from "@stanion/App/Components/Cart/CartContainerLayout";
import XngCartLineQuantity from "@stanion/App/Components/Cart/XngCartLineQuantity";
import ProductPrice, { ProductPriceStyles } from "@stanion/Overrides/Components/ProductPrice";
import React, { ChangeEvent, useContext, useState } from "react";
import { connect, ResolveThunks } from "react-redux";

interface OwnProps {
    quote: QuoteModel;
    quoteLine: QuoteLineModel;
    quoteLineClickHandler: (quoteLine: QuoteLineModel) => void;
    extendedStyles?: RfqQuoteDetailsSalesRepProductListLineStyles;
}

const mapStateToProps = (state: ApplicationState) => ({
    enableVat: getSettingsCollection(state).productSettings.enableVat,
    vatPriceDisplay: getSettingsCollection(state).productSettings.vatPriceDisplay,
    showPricing: state.context.showPricing,
});

const mapDispatchToProps = {
    updateQuoteLine,
};

type Props = OwnProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

export interface RfqQuoteDetailsSalesRepProductListLineStyles {
    mainWrapper?: InjectableCss;
    headerWrapper?: InjectableCss;
    countLabelText?: TypographyPresentationProps;
    countValueText?: TypographyPresentationProps;
    quoteAllButton?: ButtonPresentationProps;
    productImage?: ProductImageStyles;
    productBrand?: ProductBrandStyles;
    productDescription?: ProductDescriptionStyles;
    productPartNumbers?: ProductPartNumbersStyles;
    quantityHeadingAndText?: SmallHeadingAndTextStyles;
    uomHeadingAndText?: SmallHeadingAndTextStyles;
    quotedPricingText?: TypographyPresentationProps;
    quotedPricingStyles?: RfqQuoteDetailsQuotedPricingStyles;
    quoteLineButton?: ButtonPresentationProps;
    quoteCalculationHeaderText?: TypographyPresentationProps;
    quoteCalculationErrorText?: TypographyPresentationProps;
    quoteCalculationCancelButton?: ButtonPresentationProps;
    quoteCalculationApplyAllButton?: ButtonPresentationProps;
    quotedPricingContainer?: InjectableCss;
    qtyLabel?: TypographyPresentationProps;
    qtyconainer?: InjectableCss;
    buttonContainer?: InjectableCss;
    cartLineContainerStyles?: CartLineContainerStyles;
    toggleNotesLink?: LinkPresentationProps;
    notesTextArea?: TextAreaProps;
    footerWrapper?: InjectableCss;
    totalLabelText?: TypographyPresentationProps;
    totalValueText?: TypographyPresentationProps;
    productPrice?: ProductPriceStyles;
    extendedUnitNetPriceText?: SmallHeadingAndTextStyles;
    linesheader?: LineHeaderStyles;
}

export const rfqQuoteDetailsSalesRepProductListLine: RfqQuoteDetailsSalesRepProductListLineStyles = {};

const styles = rfqQuoteDetailsSalesRepProductListLine;

const RfqQuoteDetailsSalesRepProductListLine = ({
    quote,
    quoteLine,
    showPricing,
    updateQuoteLine,
    quoteLineClickHandler,
}: Props) => {
    const toasterContext = useContext(ToasterContext);

    if (!quote || !quoteLine) {
        return null;
    }
    const qtyOrderedSubmitHandler = (value: string) => {
        const inputQty = Number.parseInt(value, 10);
        if (Number.isNaN(inputQty)) {
            return;
        }

        updateQuoteLine({
            quoteId: quote.id,
            quoteLineId: quoteLine.id,
            quoteLine: {
                ...quoteLine,
                qtyOrdered: inputQty,
            },
            onSuccess: () => {
                toasterContext.addToast({ body: translate("Quantity updated"), messageType: "success" });
            },
            onComplete(resultProps) {
                if (resultProps.result?.quoteLine) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.();
                }
            },
        });
    };

    const {
        value: qtyOrdered,
        changeHandler: qtyOrderedChangeHandler,
        keyDownHandler: qtyOrderedKeyDownHandler,
        blurHandler: qtyOrderedBlurHandler,
    } = useAccessibleSubmit(quoteLine.qtyOrdered!.toString(), qtyOrderedSubmitHandler);

    const [showNotes, setShowNotes] = useState(true);
    const toggleNotesClickHandler = () => {
        setShowNotes(!showNotes);
    };

    const toggleNotesHeading = showNotes ? translate("Hide Line Notes") : translate("Show Line Notes");

    // XNG-Changes Structure
    return (
        <CartLineContainer
            key={`${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
            data-test-selector={`quoteLine_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}
            extendedStyles={styles.cartLineContainerStyles}
        >
            <CartLineContainer.Image>
                <ProductImage product={quoteLine} extendedStyles={styles.productImage} />
            </CartLineContainer.Image>
            <CartLineContainer.Info>
                {quoteLine.brand && <ProductBrand brand={quoteLine.brand} extendedStyles={styles.productBrand} />}
                <ProductDescription product={quoteLine} extendedStyles={styles.productDescription} />
                <ProductPartNumbers
                    productNumber={quoteLine.erpNumber}
                    customerProductNumber={quoteLine.customerName}
                    manufacturerItem={quoteLine.manufacturerItem}
                    extendedStyles={styles.productPartNumbers}
                />
            </CartLineContainer.Info>
            <CartLineContainer.Price>
                <ProductPrice
                    product={quoteLine}
                    currencySymbol={quote.currencySymbol}
                    showLabel={true}
                    extendedStyles={styles.productPrice}
                />
                {showPricing && (
                    <>
                        <Typography {...styles.quotedPricingText}>{translate("Quoted Pricing")}</Typography>
                        <StyledWrapper {...styles.quotedPricingContainer}>
                            <RfqQuoteDetailsQuotedPricing
                                quote={quote}
                                quoteLine={quoteLine}
                                extendedStyles={styles.quotedPricingStyles}
                            />
                            <VisuallyHidden>{translate("Quantity Requested")}</VisuallyHidden>
                            {quoteLine.unitOfMeasure && (
                                <>
                                    <VisuallyHidden>{translate("Unit of Measure")}</VisuallyHidden>
                                    <SmallHeadingAndText
                                        heading={translate("U/M")}
                                        text={quoteLine.unitOfMeasure}
                                        extendedStyles={styles.uomHeadingAndText}
                                    />
                                </>
                            )}
                        </StyledWrapper>
                    </>
                )}
            </CartLineContainer.Price>
            <CartLineContainer.Qty>
                <StyledWrapper {...styles.qtyconainer}>
                    <XngCartLineQuantity
                        onChange={qtyOrderedChangeHandler}
                        onKeyDown={qtyOrderedKeyDownHandler}
                        onBlur={qtyOrderedBlurHandler}
                        label={translate("QTY Req")}
                        disabled={quoteLine.isPromotionItem || quote.isJobQuote}
                        data-test-selector="quoteDetail_productQuantity"
                        value={qtyOrdered}
                    />
                </StyledWrapper>
            </CartLineContainer.Qty>
            <CartLineContainer.Total>
                <StyledWrapper data-test-selector={`subTotal_${quoteLine.productId}_${quoteLine.unitOfMeasure}`}>
                    {quoteLine.pricing && showPricing && (
                        <>
                            <VisuallyHidden>{translate("Subtotal")}</VisuallyHidden>
                            <SmallHeadingAndText
                                {...styles.extendedUnitNetPriceText}
                                data-test-selector="quoteline_extendedUnitNetPrice"
                                heading={translate("Total")}
                                text={quoteLine.pricing.extendedUnitNetPriceDisplay}
                            />
                        </>
                    )}
                </StyledWrapper>
            </CartLineContainer.Total>
            <CartLineContainer.RemoveAction>
                <StyledWrapper {...styles.buttonContainer}>
                    {quote.isEditable && showPricing && (
                        <Button
                            {...styles.quoteLineButton}
                            onClick={() => quoteLineClickHandler(quoteLine)}
                            data-test-selector="quoteDetails_QuoteButton"
                        >
                            {translate("Quote")}
                        </Button>
                    )}
                </StyledWrapper>
            </CartLineContainer.RemoveAction>
            <CartLineContainer.PeripheralActionsBottom>
                <Link {...styles.toggleNotesLink} onClick={toggleNotesClickHandler}>
                    {toggleNotesHeading}
                </Link>
                {showNotes && <TextArea {...styles.notesTextArea} value={quoteLine.notes} disabled={true} />}
            </CartLineContainer.PeripheralActionsBottom>
        </CartLineContainer>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(RfqQuoteDetailsSalesRepProductListLine);
